import React, { memo } from 'react';
import { Z_INDEX_TOOLTIP } from '@/modules/Popup/utils/popupPosition';
import { cn } from '@/shared/utils/cn';

export type InlineTooltipPosition = 'top' | 'bottom' | 'left' | 'right' | 'over';

export interface InlineTooltipProps {
  /** Contenido del tooltip */
  label: React.ReactNode;
  /** Si el tooltip está visible */
  isVisible: boolean;
  /** Posición del tooltip */
  position?: InlineTooltipPosition;
  /** Clase adicional para el tooltip */
  className?: string;
}

/**
 * InlineTooltip - Tooltip CSS puro para usar dentro de elementos flex
 *
 * Este componente es útil cuando el Tooltip normal interfiere con layouts flex.
 * Se posiciona absolutamente dentro del elemento padre que debe tener `position: relative`.
 *
 * @example
 * ```tsx
 * <div className="ui-relative" onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
 *   Contenido
 *   <InlineTooltip label="Tooltip" isVisible={hovered} position="over" />
 * </div>
 * ```
 */
export const InlineTooltip = memo(
  ({ label, isVisible, position = 'bottom', className }: InlineTooltipProps) => {
    // Para posición "over", no mostramos arrow
    const showArrow = position !== 'over';

    // Clases base del tooltip (mismas que el Tooltip original)
    const baseClasses = cn(
      'ui-absolute',
      'ui-py-4 ui-px-8',
      'ui-rounded-control-sm',
      'ui-text-body-xs-bold ui-font-body-xs-bold ui-tracking-body-xs ui-leading-none',
      'ui-max-w-[300px]',
      'ui-break-words',
      'ui-shadow-lg',
      'ui-transition-opacity ui-duration-150 ui-ease-in-out',
      'ui-text-on-canvas-inverse ui-bg-canvas-inverse',
      'ui-whitespace-nowrap',
      'ui-pointer-events-none',
      // Visibilidad
      isVisible ? 'ui-opacity-100 ui-visible' : 'ui-opacity-0 ui-invisible',
      className,
    );

    // Clases de posición - margen reducido (4px)
    const positionClasses: Record<InlineTooltipPosition, string> = {
      top: 'ui-bottom-full ui-left-1/2 -ui-translate-x-1/2 ui-mb-4',
      bottom: 'ui-top-full ui-left-1/2 -ui-translate-x-1/2 ui-mt-4',
      left: 'ui-right-full ui-top-1/2 -ui-translate-y-1/2 ui-mr-4',
      right: 'ui-left-full ui-top-1/2 -ui-translate-y-1/2 ui-ml-4',
      over: 'ui-top-1/2 ui-left-1/2 -ui-translate-x-1/2 -ui-translate-y-1/2',
    };

    // Estilos del arrow (cuadrado rotado como el Tooltip original)
    const arrowSize = 8;
    const arrowOffset = arrowSize / 2;
    const arrowColor = 'var(--ui-color-bg-canvas-inverse)';

    const arrowBaseStyles: React.CSSProperties = {
      position: 'absolute',
      width: `${arrowSize}px`,
      height: `${arrowSize}px`,
      backgroundColor: arrowColor,
      borderRadius: '2.5px',
    };

    const arrowPositionStyles: Record<
      Exclude<InlineTooltipPosition, 'over'>,
      React.CSSProperties
    > = {
      top: {
        ...arrowBaseStyles,
        bottom: `-${arrowOffset}px`,
        left: '50%',
        transform: 'translateX(-50%) rotate(45deg)',
      },
      bottom: {
        ...arrowBaseStyles,
        top: `-${arrowOffset}px`,
        left: '50%',
        transform: 'translateX(-50%) rotate(45deg)',
      },
      left: {
        ...arrowBaseStyles,
        right: `-${arrowOffset}px`,
        top: '50%',
        transform: 'translateY(-50%) rotate(45deg)',
      },
      right: {
        ...arrowBaseStyles,
        left: `-${arrowOffset}px`,
        top: '50%',
        transform: 'translateY(-50%) rotate(45deg)',
      },
    };

    const tooltipStyle: React.CSSProperties = {
      zIndex: Z_INDEX_TOOLTIP,
    };

    return (
      <div
        className={cn(baseClasses, positionClasses[position])}
        style={tooltipStyle}
        role="tooltip"
        aria-hidden={!isVisible}
      >
        {label}
        {showArrow && (
          <div style={arrowPositionStyles[position as Exclude<InlineTooltipPosition, 'over'>]} />
        )}
      </div>
    );
  },
);

InlineTooltip.displayName = 'InlineTooltip';
